import React, { useEffect } from 'react'
import Router from 'next/router'
import { useSelector } from 'react-redux'
import { AiOutlineDownload } from 'react-icons/ai'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import { ActionButton } from '../styles'
import { isMobile } from 'react-device-detect'
import qs from 'qs'
import { configSelectors } from '@/store/configs'
import { showInfo } from './toaster'
import { customerId } from '@/utils/env'
import { customerHelper } from '@tweddle-se-team/common/constants'
import DownloadIcon from '@/components/themes/common/icons/downloadIcon'

const { CUSTOMER_IDS, PUBLICATION_RETRIEVAL_CRITERIA } = customerHelper

const openPdfWithIframeSupport = ({ path, isInFrame, domain }) => {
  if (isInFrame) {
    window.open(`${domain}#${path}`, '_blank')
    return
  }

  return Router.push(path)
}

const openPdfByCustomer = {
  [CUSTOMER_IDS.TME]: openPdfWithIframeSupport,
  [CUSTOMER_IDS.SUBARU]: openPdfWithIframeSupport,
  [CUSTOMER_IDS.NORDICS]: ({ path }) => Router.push(path)
}

const waitingContent = {}
const download =
  (criteria, contentType, isInFrame = false, iframeDomain) =>
  async () => {
    const { partNumber, ...restCriteria } = criteria

    if (isMobile && contentType !== 'webview') {
      const url = await apiWrapper.get('publications/content/download', { partNumber, contentType })
      // Safari/Chrome (iOS) is blocking any call to window.open() which is made inside an async call.
      // setTimeout code runs on the main thread, instead of the asynchronous one, thats why we need to it here.
      setTimeout(() => window.open(url, '_blank'), 0)
      return
    }

    const query = { partNumber, contentType }
    const path = restCriteria ? `/pdf/${partNumber}?${qs.stringify(restCriteria)}` : `/pdf/${partNumber}`

    if (contentType === 'webview') {
      const openPdf = openPdfByCustomer[customerId] || openPdfByCustomer[CUSTOMER_IDS.TME]
      await openPdf({ path, isInFrame, domain: iframeDomain })
      return
    }
    const getUploadStatus = () => get('publications/content/uploadStatus', query)
    const getContent = () => get('publications/content/download', query)
    const download = async () => {
      window.location.href = await getContent()
    }

    if ((await getUploadStatus()).isAvailable) await download()
    else {
      showInfo(
        'We are processing your request. Your download will start shortly. You can choose to:\n' +
          '* Stay on this page until your download starts automatically.\n' +
          '* Or browse other pages and return at a later time. Simply click the download button of the requested file ' +
          'again and your download will start immediately if your request was processed.'
      )
      const contentInterval = `${partNumber}_${contentType}`
      if (waitingContent[contentInterval]) return
      await getContent()
      waitingContent[contentInterval] = setInterval(async () => {
        const { isAvailable } = await getUploadStatus()
        if (isAvailable) {
          clearInterval(waitingContent[contentInterval])
          delete waitingContent[contentInterval]
          await download()
        }
      }, 3000)
    }
  }

const DownloadButton = ({ publication, circle, className, withMarginLeft = true, ...props }) => {
  const { isInFrame, iframeDomain } = useSelector(configSelectors.selectConfigs)
  const {
    _id,
    contents: { webview }
  } = publication

  useEffect(
    () => () => {
      for (const [content, intervalId] of Object.entries(waitingContent)) {
        clearInterval(intervalId)
        delete waitingContent[content]
      }
    },
    [_id]
  )

  // Build criteria based on PUBLICATION_RETRIEVAL_CRITERIA and customerId
  const criteriaFields = PUBLICATION_RETRIEVAL_CRITERIA[customerId] || PUBLICATION_RETRIEVAL_CRITERIA['TME']
  const criteria = {}
  for (const field of criteriaFields) {
    if (publication[field] !== undefined) {
      criteria[field] = publication[field]
    }
  }

  return (
    <>
      {webview && (
        <ActionButton
          onClick={download(criteria, 'webview', isInFrame, iframeDomain)}
          placement="right"
          appearance="ghost"
          $withIcon
          style={{ marginLeft: withMarginLeft ? 10 : 0 }}
          circle={circle}
          className={className}
          {...props}
        >
          PDF <DownloadIcon defaultIcon={AiOutlineDownload} size={16} className="fix-btn-icon" />
        </ActionButton>
      )}
    </>
  )
}

export default DownloadButton
